import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ContestType } from '@betterpool/api-types/contests-service';

import { useLeagues } from '~/domains/common/hooks/useLeagues';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { ContestStatus } from '~/domains/contest/domains/services/enums/contests-status.enum';
import { getCommissionerContestsV2 } from '~/services/contests/contests-v2.service.api';
import type { GetCommissionerContestsV2Params } from '~/services/contests/contests-v2.service.api';
import { STALE_TIME } from '~/domains/contest/constants/query';
import { isLoadingFixedForDisabledQuery } from '~/domains/common/utils/isLoadingFixedForEnabled';
import type { SortDirection } from '~/domains/common/enums/sort-direction.enum';
import { SortOptions } from '~/services/contests/enums/contest-sort-options.enum';
import { ActiveTab } from '~/domains/commissioner/enums';

import parseCommissionerContestsV2 from './parseCommissionerContestsV2';

const PAGE_SIZE = 20;

const SORT_OPTION_TO_V2_SORT: Partial<
  Record<SortOptions, GetCommissionerContestsV2Params['sort']>
> = {
  [SortOptions.START_DATE]: 'start_date',
  [SortOptions.ENTRY_FEE]: 'entry_fee',
  [SortOptions.TOTAL_PRIZES]: 'total_prizes',
  [SortOptions.ENTRANTS_COUNT]: 'entry_count',
  [SortOptions.CONTEST_NAME]: 'name',
};

export const useChannelContestsV2 = ({
  activeTab,
  contestStatus,
  enabled = true,
}: {
  activeTab?: ActiveTab;
  contestStatus?: ContestStatus;
  enabled?: boolean;
}) => {
  const router = useRouter();
  const currentQuery = { ...router.query };
  const { data: leagues, isLoading: isLeaguesLoading } = useLeagues();

  const URLContestStatusFilter = currentQuery[URLQueryKeys.CONTEST_STATUS] as ContestStatus;
  const URLLeagueFilter = currentQuery[URLQueryKeys.LEAGUES] as string;
  const URLContestTypeFilter = currentQuery[URLQueryKeys.CONTEST_TYPE] as ContestType;
  const URLSortBy = currentQuery[URLQueryKeys.SORT_BY] as SortOptions;
  const URLSortByOrder = currentQuery[URLQueryKeys.SORT_ORDER] as SortDirection;
  const leagueFilter = URLLeagueFilter
    ? leagues?.find((league) => league.alias === URLLeagueFilter)?.alias
    : undefined;
  const URLRestartStatusFilter = currentQuery[URLQueryKeys.RESTART_STATUS] as string;

  const tab: GetCommissionerContestsV2Params['tab'] = activeTab === ActiveTab.FINAL ? 'final' : 'active';

  const effectiveStatus = contestStatus ?? URLContestStatusFilter;
  const status: GetCommissionerContestsV2Params['status'] = (() => {
    if (tab === 'final') return undefined;
    if (effectiveStatus === ContestStatus.SCHEDULED) return 'not_yet_started';
    if (effectiveStatus === ContestStatus.IN_PROGRESS) return 'started';
    return undefined;
  })();

  const queryKey = [
    'channel/contests/v2',
    tab,
    status,
    URLLeagueFilter,
    URLContestTypeFilter,
    URLRestartStatusFilter,
    URLSortBy,
    URLSortByOrder,
    leagueFilter,
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    fetchStatus,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    enabled: enabled && (!URLLeagueFilter || !!leagueFilter || !isLeaguesLoading),
    keepPreviousData: true,
    queryFn: async ({ pageParam = 0 }) =>
      getCommissionerContestsV2({
        tab,
        status,
        league: leagueFilter,
        contestType: URLContestTypeFilter,
        needsRestart: URLRestartStatusFilter === 'true' ? 'true' : undefined,
        sort: URLSortBy ? SORT_OPTION_TO_V2_SORT[URLSortBy] : undefined,
        order: URLSortByOrder as GetCommissionerContestsV2Params['order'],
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.offset + PAGE_SIZE;
      if (nextPage > lastPage.total) {
        return undefined;
      }
      return nextPage;
    },
    staleTime: STALE_TIME,
  });

  const contests = useMemo(
    () => parseCommissionerContestsV2(data?.pages?.flatMap((page) => page.data) ?? []),
    [data?.pages]
  );

  return {
    queryKey,
    contests,
    totalCount: data?.pages[0]?.total,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingFixedForDisabledQuery(isLoading, fetchStatus),
    isFetching: isFetching && !isFetchingNextPage,
  };
};
